import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  DollarSign, MessageSquare,
  TrendingUp, Clock, AlertCircle,
  Eye, ChevronRight, Calendar,
  RefreshCw, ExternalLink,
  Instagram, Globe, Sparkles, Linkedin,
  Hash, Image, X, Lock, Phone, Bell, BarChart3, Send
} from 'lucide-react';
import AdminNotifications from '../components/AdminNotifications';
import AdminThemeToggle from '../components/AdminThemeToggle';
import DevUpdateControls from '../components/DevUpdateControls';
import InstallAppSection from '../components/InstallAppSection';
import PackagePrediction from '../components/PackagePrediction';
import ExpenseTrends from '../components/ExpenseTrends';
import PushNotificationToggle from '../components/PushNotificationToggle';
import RecentConsultations from '../components/RecentConsultations';
import AdminQaToast from '../components/AdminQaToast';
import '../styles/Admin.css';

// Platform icons/colors for media posts
const PLATFORMS = {
  instagram: { icon: Instagram, color: '#E1306C', label: 'Instagram' },
  linkedin: { icon: Linkedin, color: '#0077B5', label: 'LinkedIn' },
  tiktok: { icon: Hash, color: '#000000', label: 'TikTok' },
  all: { icon: Image, color: '#8B5CF6', label: 'All Platforms' }
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, token, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [upcomingPosts, setUpcomingPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('month'); // 'week' or 'month'
  const [consultationStats, setConsultationStats] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [sendingUpdate, setSendingUpdate] = useState(false);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [sendPreview, setSendPreview] = useState(null); // preview payload for the Send Update modal
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reportQaUnread, setReportQaUnread] = useState(0);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    
    try {
      const result = await login(loginEmail, loginPassword);
      if (!result.success) {
        setLoginError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      setLoginError('Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchUpcomingPosts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/content-schedule/upcoming/reminders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Combine today and tomorrow posts, limit to 3
        const allPosts = [...(data.today || []), ...(data.tomorrow || [])].slice(0, 3);
        setUpcomingPosts(allPosts);
      }
    } catch (err) {
      console.error('Failed to fetch upcoming posts:', err);
    }
  }, [token]);

  const fetchExpenseSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/expenses/summary?period=${statsPeriod}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExpenseSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch expense summary:', err);
    }
  }, [token, statsPeriod]);

  const fetchConsultationStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/consultation-stats?period=${statsPeriod}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConsultationStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch consultation stats:', err);
    }
  }, [token, statsPeriod]);

  const fetchLeadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chatbot/admin/lead-stats`);
      if (response.ok) {
        setLeadStats(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch lead stats:', err);
    }
  }, []);

  const fetchReportQaUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/clinical-report-threads/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReportQaUnread(data.total_unread || 0);
      }
    } catch (err) {
      // Non-fatal — badge simply won't show.
    }
  }, [token]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 403) {
        setError('Access denied. Admin privileges required.');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      
      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    fetchDashboard();
    fetchUpcomingPosts();
    fetchExpenseSummary();
    fetchConsultationStats();
    fetchLeadStats();
    fetchReportQaUnread();
    const id = setInterval(fetchReportQaUnread, 30000);
    return () => clearInterval(id);
  }, [isAuthenticated, fetchDashboard, fetchUpcomingPosts, fetchExpenseSummary, fetchConsultationStats, fetchLeadStats, fetchReportQaUnread]);

  // Clicking "Send Update" now opens a preview modal first.
  // User confirms what's being sent, then hits "Send Now" to actually dispatch.
  const handleSendDevUpdate = async () => {
    setPreviewLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dev-updates/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSendPreview(await response.json());
      } else {
        alert('❌ Could not fetch preview. Try again.');
      }
    } catch (err) {
      console.error('Preview failed:', err);
      alert('❌ Could not fetch preview. Try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Actually send after the user confirms in the modal
  const confirmSendDevUpdate = async () => {
    setSendingUpdate(true);
    try {
      const response = await fetch(`${API_URL}/api/dev-updates/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setSendPreview(null);
        alert('✅ Development update email sent to team!');
      } else {
        const data = await response.json();
        alert(`❌ Failed to send: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to send dev update:', err);
      alert('❌ Failed to send update email');
    } finally {
      setSendingUpdate(false);
    }
  };

  // Send the weekly business roll-up on demand
  const handleSendWeekly = async () => {
    if (!window.confirm('Send the weekly business roll-up (leads, consults, revenue) to the team now?')) return;
    setSendingWeekly(true);
    try {
      const response = await fetch(`${API_URL}/api/dev-updates/send-weekly`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        alert('✅ Weekly roll-up email sent to team!');
      } else {
        const data = await response.json();
        alert(`❌ Failed to send: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to send weekly roll-up:', err);
      alert('❌ Failed to send weekly roll-up');
    } finally {
      setSendingWeekly(false);
    }
  };
  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-login-wrapper">
          <div className="admin-login-box">
            <div className="admin-login-header">
              <img 
                src="/cadence-crest.png" 
                alt="CADENCE" 
                className="admin-login-crest"
              />
              <h1>Admin Portal</h1>
              <p>Authorized Personnel Only</p>
            </div>
            
            <form onSubmit={handleAdminLogin} className="admin-login-form">
              {loginError && (
                <div className="admin-login-error">
                  <AlertCircle size={16} />
                  {loginError}
                </div>
              )}
              
              <div className="admin-login-field">
                <label htmlFor="admin-email">Email Address</label>
                <input
                  id="admin-email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="Enter your admin email"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              
              <div className="admin-login-field">
                <label htmlFor="admin-password">Password</label>
                <input
                  id="admin-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              
              <button 
                type="submit" 
                className="admin-login-btn"
                disabled={loginLoading}
                data-testid="admin-login-btn"
              >
                {loginLoading ? 'Authenticating...' : 'Access Admin Portal'}
              </button>
            </form>
            
            <div className="admin-login-footer">
              <Link to="/">← Return to Main Site</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Access Denied</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="admin-btn primary">
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <RefreshCw className="spin" size={32} />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="admin-page-wrapper">
      <AdminQaToast token={token} />
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <div className="admin-nav-links">
              <Link to="/" className="admin-logo-link">
                <img src="/cadence-gold-plate.png" alt="CADENCE" className="admin-logo" />
              </Link>
              <div className="admin-breadcrumb">
                <Link to="/" className="admin-back-link">Website</Link>
                <span className="nav-separator">|</span>
                <Link to="/portal" className="admin-back-link">Patient Portal</Link>
              </div>
            </div>
            <h1>Admin Dashboard</h1>
            <p className="admin-subtitle">
              Welcome back, {user?.first_name || 'Admin'}
            </p>
          </div>
          <div className="admin-header-actions">
            <select 
              value={statsPeriod} 
              onChange={(e) => setStatsPeriod(e.target.value)}
              className="admin-period-select"
              data-testid="stats-period-filter"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
            <PushNotificationToggle />
            <AdminThemeToggle />
            <AdminNotifications />
            <button onClick={fetchDashboard} className="admin-btn secondary">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

      {/* Expense Overview - Simple Tiles */}
      <div className="admin-stats-grid expense-focused">
        <div 
          className="admin-stat-card clickable expense-total" 
          onClick={() => navigate('/admin/expenses')}
          data-testid="total-expenses-tile"
        >
          <div className="stat-icon expense">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value expense-amount">
              {expenseSummary ? formatCurrency(expenseSummary.total_invested) : '—'}
            </span>
            <span className="stat-label">Total Invested</span>
          </div>
          <ChevronRight className="stat-arrow" size={18} />
        </div>

        <div 
          className="admin-stat-card clickable expense-recurring" 
          onClick={() => navigate('/admin/expenses?tab=recurring')}
          data-testid="recurring-expenses-tile"
        >
          <div className="stat-icon recurring">
            <RefreshCw size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value expense-amount">
              {expenseSummary ? `${formatCurrency(expenseSummary.monthly_burn_rate)}/mo` : '—'}
            </span>
            <span className="stat-label">Monthly Burn Rate</span>
          </div>
          <ChevronRight className="stat-arrow" size={18} />
        </div>

        {/* Consultation Requests Ticker */}
        <div 
          className="admin-stat-card clickable consultation-requests" 
          onClick={() => navigate('/admin/messages')}
          data-testid="consultation-requests-tile"
        >
          <div className="stat-icon consultation">
            <Phone size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value consultation-count">
              {consultationStats ? consultationStats.period_requests : '—'}
            </span>
            <span className="stat-label">
              Consultation Requests
              <span className="stat-period">{consultationStats?.period_label || 'This Month'}</span>
            </span>
          </div>
          {consultationStats?.today_requests > 0 && (
            <div className="stat-badge pulse">
              <Bell size={14} />
              <span>{consultationStats.today_requests} Today</span>
            </div>
          )}
          <ChevronRight className="stat-arrow" size={18} />
        </div>

        {/* Ask CADENCE — new leads this week */}
        <div
          className="admin-stat-card clickable consultation-requests"
          onClick={() => navigate('/admin/inquiries')}
          data-testid="chat-leads-tile"
        >
          <div className="stat-icon consultation">
            <MessageSquare size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value consultation-count">
              {leadStats ? leadStats.new_this_week : '—'}
            </span>
            <span className="stat-label">
              New Ask CADENCE Leads
              <span className="stat-period">This Week</span>
            </span>
          </div>
          {leadStats?.pending_leads > 0 && (
            <div className="stat-badge pulse">
              <Bell size={14} />
              <span>{leadStats.pending_leads} Pending</span>
            </div>
          )}
          <ChevronRight className="stat-arrow" size={18} />
        </div>
      </div>

      {/* Interest breakdown — what this week's leads want */}
      {leadStats?.interest_breakdown?.length > 0 && (
        <div className="admin-card" data-testid="lead-interest-breakdown" style={{ marginTop: '1rem' }}>
          <h3><TrendingUp size={18} /> What Leads Are Interested In</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '0.75rem' }}>
            {leadStats.interest_breakdown.map((row, idx) => (
              <span
                key={idx}
                data-testid={`interest-chip-${idx}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.9rem', borderRadius: '999px',
                  background: 'linear-gradient(135deg, #4A0E4E 0%, #2D0A31 100%)',
                  color: '#F0D488', border: '1px solid #C9A227',
                  fontSize: '0.85rem', fontWeight: 600,
                }}
              >
                {row.interest}
                <span style={{
                  background: '#D4AF37', color: '#2D0A31', borderRadius: '999px',
                  padding: '0 0.5rem', fontWeight: 700, fontSize: '0.8rem',
                }}>{row.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links Section */}
      <div className="admin-card quick-links-section">
        <h3><Sparkles size={18} /> Quick Links</h3>
        <div className="quick-links-grid">
          <a 
            href="/coming-soon" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="quick-link-card"
            data-testid="view-landing-page-link"
          >
            <div className="quick-link-icon landing">
              <Globe size={24} />
            </div>
            <div className="quick-link-content">
              <span className="quick-link-title">View Public Site</span>
              <span className="quick-link-desc">See what visitors see</span>
            </div>
            <ExternalLink size={16} className="external-icon" />
          </a>
          
          <a 
            href="/compass-preview" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="quick-link-card"
            data-testid="view-welcome-link"
          >
            <div className="quick-link-icon waitlist">
              <TrendingUp size={24} />
            </div>
            <div className="quick-link-content">
              <span className="quick-link-title">CADENCE Compass</span>
              <span className="quick-link-desc">Free health guides</span>
            </div>
            <ExternalLink size={16} className="external-icon" />
          </a>
          
          <a 
            href="https://search.google.com/search-console?resource_id=sc-domain:cadencemedicalconsulting.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="quick-link-card analytics-link"
            data-testid="site-analytics-link"
          >
            <div className="quick-link-icon analytics">
              <BarChart3 size={24} />
            </div>
            <div className="quick-link-content">
              <span className="quick-link-title">Site Analytics</span>
              <span className="quick-link-desc">Traffic • Visits • Search Performance</span>
            </div>
            <ExternalLink size={16} className="external-icon" />
          </a>
          
          <button 
            onClick={handleSendDevUpdate}
            disabled={previewLoading || sendingUpdate}
            className="quick-link-card send-update-link"
            data-testid="send-dev-update-btn"
          >
            <div className="quick-link-icon send-update">
              <Send size={24} />
            </div>
            <div className="quick-link-content">
              <span className="quick-link-title">
                {previewLoading ? 'Loading...' : sendingUpdate ? 'Sending...' : 'Send Update'}
              </span>
              <span className="quick-link-desc">Preview & send daily summary</span>
            </div>
          </button>

          <button 
            onClick={handleSendWeekly}
            disabled={sendingWeekly}
            className="quick-link-card send-update-link"
            data-testid="send-weekly-rollup-btn"
          >
            <div className="quick-link-icon send-update">
              <TrendingUp size={24} />
            </div>
            <div className="quick-link-content">
              <span className="quick-link-title">
                {sendingWeekly ? 'Sending...' : 'Send Weekly Roll-up'}
              </span>
              <span className="quick-link-desc">Leads, consults & revenue (7 days)</span>
            </div>
          </button>
          {/* Daily update email controls — mute, resume, dry-run preview */}
          <DevUpdateControls />
          {/* Install CADENCE app pill — one-tap on Chrome/Edge/Android, expandable iOS instructions */}
          <InstallAppSection />
        </div>
        
        {/* Upcoming Media Posts Preview */}
        {upcomingPosts.length > 0 && (
          <div className="upcoming-posts-section">
            <h4><Instagram size={16} /> Upcoming Social Posts</h4>
            <div className="upcoming-posts-list">
              {upcomingPosts.map(post => {
                const platform = PLATFORMS[post.platform] || PLATFORMS.all;
                const isToday = post.scheduled_date === new Date().toISOString().split('T')[0];
                return (
                  <div 
                    key={post.id} 
                    className="upcoming-post-card"
                    onClick={() => setSelectedPost(post)}
                    data-testid={`preview-post-${post.id}`}
                  >
                    <div className="post-platform-icon" style={{ backgroundColor: platform.color }}>
                      <platform.icon size={16} />
                    </div>
                    <div className="post-info">
                      <span className="post-title">{post.title}</span>
                      <span className="post-time">
                        {isToday ? 'Today' : 'Tomorrow'} at {post.scheduled_time || '10:00'}
                      </span>
                    </div>
                    <button className="preview-btn" title="Preview Post">
                      <Eye size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
            <button 
              onClick={() => navigate('/admin/calendar')} 
              className="view-all-posts-btn"
              data-testid="view-all-scheduled-posts"
            >
              View All Scheduled Posts <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Recent Consultation Requests Panel */}
      {consultationStats?.recent_requests?.length > 0 && (
        <RecentConsultations 
          requests={consultationStats.recent_requests}
          onViewAll={() => navigate('/admin/messages')}
        />
      )}

      {/* Quick Actions & Recent Activity */}
      <div className="admin-content-grid">
        {/* Quick Actions - Simplified */}
        <div className="admin-card quick-actions">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button 
              onClick={() => navigate('/admin/expenses')} 
              className="action-btn highlight-gold"
              data-testid="admin-view-expenses-btn"
            >
              <DollarSign size={20} />
              <span>Manage Expenses</span>
            </button>
            
            <button 
              onClick={() => navigate('/admin/waitlist')} 
              className="action-btn"
              data-testid="admin-view-waitlist-btn"
            >
              <TrendingUp size={20} />
              <span>Waitlist</span>
            </button>
          </div>
          
          {/* Communication */}
          <h4 className="section-divider">Communication</h4>
          <div className="action-buttons">
            <button 
              onClick={() => navigate('/admin/messages')} 
              className="action-btn"
              data-testid="admin-view-messages-btn"
            >
              <MessageSquare size={20} />
              <span>Message Center</span>
              {reportQaUnread > 0 && (
                <span
                  className="action-btn-badge"
                  data-testid="admin-message-center-badge"
                  title={`${reportQaUnread} unread patient question${reportQaUnread === 1 ? '' : 's'} on clinical reports`}
                >
                  {reportQaUnread}
                </span>
              )}
            </button>
            
            <button 
              onClick={() => navigate('/admin/calendar')} 
              className="action-btn"
              data-testid="admin-view-calendar-btn"
            >
              <Calendar size={20} />
              <span>Calendar</span>
            </button>
          </div>
        </div>

        {/* Expense Summary Card */}
        <div className="admin-card expense-summary-dashboard">
          <div className="card-header">
            <h3><DollarSign size={18} /> Expense Summary</h3>
            <button 
              onClick={() => navigate('/admin/expenses')}
              className="view-all-link"
            >
              View All <ChevronRight size={16} />
            </button>
          </div>
          <div className="expense-dashboard-content">
            <p className="expense-dashboard-note">
              Track your startup investments, recurring subscriptions (AI tools, insurance, hosting), and one-time costs.
            </p>
            <button 
              onClick={() => navigate('/admin/expenses')} 
              className="admin-btn primary"
              data-testid="go-to-expenses-btn"
            >
              <DollarSign size={16} /> Open Expense Tracker
            </button>
          </div>
        </div>
      </div>

      {/* Package Sales Prediction */}
      <PackagePrediction expenseSummary={expenseSummary} />

      {/* Expense Trends & Analytics */}
      <ExpenseTrends />

      {/* Media Post Preview Modal */}
      {selectedPost && (
        <div className="modal-overlay" onClick={() => setSelectedPost(null)}>
          <div className="modal-content media-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Post Preview</h3>
              <button onClick={() => setSelectedPost(null)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Platform Badge */}
              <div className="preview-platform-badge">
                {(() => {
                  const platform = PLATFORMS[selectedPost.platform] || PLATFORMS.all;
                  return (
                    <span className="platform-tag" style={{ backgroundColor: platform.color }}>
                      <platform.icon size={16} /> {platform.label}
                    </span>
                  );
                })()}
                <span className="preview-status">
                  {selectedPost.status === 'posted' ? '✓ Posted' : `Scheduled: ${selectedPost.scheduled_date}`}
                </span>
              </div>
              
              {/* Post Title */}
              <h4 className="preview-title">{selectedPost.title}</h4>
              
              {/* Post Type */}
              <div className="preview-meta">
                <span className="post-type-badge">{selectedPost.post_type || 'Post'}</span>
                {selectedPost.scheduled_time && (
                  <span className="post-time-badge">
                    <Clock size={14} /> {selectedPost.scheduled_time}
                  </span>
                )}
              </div>
              
              {/* Image Preview */}
              {selectedPost.image_url && (
                <div className="preview-image-container">
                  <img 
                    src={selectedPost.image_url} 
                    alt={selectedPost.title}
                    className="preview-image"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              
              {/* Caption */}
              {selectedPost.caption && (
                <div className="preview-caption">
                  <label>Caption</label>
                  <p>{selectedPost.caption}</p>
                </div>
              )}
              
              {/* Hashtags */}
              {selectedPost.hashtags && (
                <div className="preview-hashtags">
                  <label>Hashtags</label>
                  <p>{selectedPost.hashtags}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                onClick={() => {
                  setSelectedPost(null);
                  navigate('/admin/calendar');
                }} 
                className="admin-btn secondary"
              >
                <Calendar size={16} /> Go to Calendar
              </button>
              <button 
                onClick={() => setSelectedPost(null)} 
                className="admin-btn primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Update — preview & confirm modal */}
      {sendPreview && (
        <div
          className="modal-overlay"
          onClick={() => !sendingUpdate && setSendPreview(null)}
          data-testid="send-update-preview-modal"
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Preview — Daily Update Email</h3>
              <button
                onClick={() => !sendingUpdate && setSendPreview(null)}
                className="close-btn"
                aria-label="Close preview"
                data-testid="send-update-preview-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p
                className={
                  sendPreview.would_send
                    ? 'preview-banner preview-banner-ok'
                    : 'preview-banner preview-banner-warn'
                }
              >
                {sendPreview.would_send
                  ? '✓ Ready to send'
                  : '⚠ Scheduler would skip this one — sending manually will still work'}
                <span className="preview-reason"> · {sendPreview.reason}</span>
              </p>

              {sendPreview.entries && sendPreview.entries.length > 0 ? (
                <div className="preview-entries">
                  <div className="preview-entries-label">
                    {sendPreview.entries.length} entries will be included:
                  </div>
                  <ul>
                    {sendPreview.entries.map((entry, idx) => (
                      <li key={idx}>
                        <strong>{entry.category}</strong>
                        {entry.description ? (
                          <span className="preview-entry-date"> ({entry.description})</span>
                        ) : null}
                        {entry.details && entry.details.length > 0 && (
                          <div className="preview-entry-count">
                            {entry.details.length} detail
                            {entry.details.length === 1 ? '' : 's'}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="preview-empty">
                  No recent PRD entries found. Sending will still fire a minimal update
                  with "no changes to report".
                </p>
              )}

              <p className="preview-recipients">
                Will be sent to: <strong>info@cadencemedicalconsulting.com</strong>
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setSendPreview(null)}
                disabled={sendingUpdate}
                className="admin-btn"
                data-testid="send-update-preview-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmSendDevUpdate}
                disabled={sendingUpdate}
                className="admin-btn primary"
                data-testid="send-update-preview-confirm"
              >
                <Send size={14} style={{ marginRight: '0.35rem' }} />
                {sendingUpdate ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminDashboard;
